import { Box, Button, IconButton, TextField } from '@mui/material';
import UpAndDownFilterIcon from 'assets/images/iPrise/UpAndDownFilter.svg';
import { useDebounce } from 'hooks';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

import AddAndEditJobV2 from '../../../common/AddAndEditJobV2';
import SortMenu from './SortMenu';

const JobQueueHeader: React.FC = () => {
  const [openAddJob, setOpenAddJob] = useState(false);
  const { recruiterJobQueueService } = useStore();
  const { searchTerm, selectedSort } = recruiterJobQueueService;
  const [sortAnchorEl, setAnchorEl] = useState(null);
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const debouncedSearch = useDebounce(localSearch, 500);

  const [localPercentage, setLocalPercentage] = useState(
    recruiterJobQueueService.percentage,
  );
  const debouncedPercentage = useDebounce(localPercentage, 500);

  // Sync debounced search to store
  useEffect(() => {
    recruiterJobQueueService.setSearchTerm(debouncedSearch);
  }, [debouncedSearch, recruiterJobQueueService]);

  // Sync debounced percentage to store
  useEffect(() => {
    recruiterJobQueueService.setPercentage(debouncedPercentage);
  }, [debouncedPercentage, recruiterJobQueueService]);

  const handleSortClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleSortClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSortSelect = useCallback(
    (sort: string) => {
      recruiterJobQueueService.setSelectedSort(sort);
      setAnchorEl(null);
    },
    [recruiterJobQueueService],
  );

  const onSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
  }, []);

  const handlePercentageChange = useCallback((value: string) => {
    setLocalPercentage(value);
  }, []);

  return (
    <Box className="main-tabs-container">
      <Box className="header-left">
        <span className="header-title">Job Queue</span>
      </Box>
      <Box className="header-actions">
        <TextField
          placeholder="Search"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          className="search-field"
          size="small"
        />
        <IconButton className="sort-button" onClick={handleSortClick}>
          <img src={UpAndDownFilterIcon} alt="Sort" />
        </IconButton>
        <SortMenu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
          selectedSort={selectedSort}
          onSelect={handleSortSelect}
        />
        <Box className="percentage-input-container">
          <TextField
            value={localPercentage}
            onChange={(e) => handlePercentageChange(e.target.value)}
            className="percentage-input"
            placeholder="0"
            size="small"
            InputProps={{
              endAdornment: (
                <Box
                  component="span"
                  sx={{ color: '#5E6C84', fontSize: '13px', width: '20px' }}>
                  %
                </Box>
              ),
            }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenAddJob(true)}
          sx={{
            '&:hover': { bgcolor: '#374151', boxShadow: 'none' },
            bgcolor: '#111827',
            borderRadius: '4px',
            boxShadow: 'none',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            height: '32px',
            minWidth: '90px',
            ml: 2,
            textTransform: 'none',
          }}>
          + Add Job
        </Button>
      </Box>
      <AddAndEditJobV2
        open={openAddJob}
        onClose={() => setOpenAddJob(false)}
        scroll="paper"
      />
    </Box>
  );
};

export default observer(JobQueueHeader);
